import React, { useState, useEffect, useRef } from 'react';
import { Send, X } from 'lucide-react';
import { useTheme } from '../../utils/theme';
import { useTranslation } from '../../utils/i18n';
import { marked } from 'marked';

export function ChatWindow({ session, onClose, onSendMessage, messages, isConnected, error, isTyping }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const isDark = theme === 'dark';

  useEffect(() => {
    marked.setOptions({
      breaks: true,
      gfm: true,
    });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const renderMarkdown = (text) => {
    return { __html: marked(text) };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  return (
    <div
      className={`fixed bottom-24 right-6 z-50 flex h-[500px] w-[380px] flex-col rounded-2xl shadow-2xl ${
        isDark
          ? 'bg-blue-950 border border-blue-800'
          : 'bg-white border border-indigo-200'
      }`}
    >
      <div
        className={`flex items-center justify-between rounded-t-2xl px-4 py-3 ${
          isDark
            ? 'bg-blue-900 border-b border-blue-800'
            : 'bg-indigo-600 border-b border-indigo-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-400'}`} />
          <h3 className="text-sm font-semibold text-white">
            {t('chat.support')}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-white/10 rounded-lg p-1 transition-colors"
          aria-label="Close chat"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className={`text-center text-sm ${isDark ? 'text-blue-300' : 'text-gray-500'}`}>
            {t('chat.no_messages')}
          </div>
        )}

        {messages.map((msg, index) => {
          const isUser = msg.sender_type === 'user';
          return (
            <div
              key={msg.id || index}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                  isUser
                    ? isDark
                      ? 'bg-white text-blue-900'
                      : 'bg-indigo-600 text-white'
                    : isDark
                    ? 'bg-blue-900 text-white'
                    : 'bg-indigo-100 text-gray-900'
                }`}
              >
                <div
                  className="break-words prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_strong]:font-semibold [&_p]:my-1"
                  dangerouslySetInnerHTML={renderMarkdown(msg.message)}
                />
                <p
                  className={`mt-1 text-xs ${
                    isUser
                      ? isDark
                        ? 'text-blue-700'
                        : 'text-indigo-100'
                      : isDark
                      ? 'text-blue-300'
                      : 'text-gray-500'
                  }`}
                >
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="flex justify-start">
            <div
              className={`rounded-2xl px-4 py-3 ${
                isDark ? 'bg-blue-900' : 'bg-indigo-100'
              }`}
            >
              <div className="flex items-center gap-1">
                <div className={`h-2 w-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-600'}`} style={{ animationDelay: '0ms' }} />
                <div className={`h-2 w-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-600'}`} style={{ animationDelay: '150ms' }} />
                <div className={`h-2 w-2 rounded-full animate-bounce ${isDark ? 'bg-white' : 'bg-gray-600'}`} style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div
          className={`mx-4 mb-2 rounded-lg border px-3 py-2 text-xs ${
            isDark
              ? 'bg-red-900/30 border-red-700 text-red-200'
              : 'bg-rose-50 border-rose-300 text-rose-800'
          }`}
        >
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={`flex items-center gap-2 border-t p-4 ${
          isDark ? 'border-blue-800' : 'border-indigo-200'
        }`}
      >
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder={t('chat.type_message')}
          className={`flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
            isDark
              ? 'bg-blue-900 border border-blue-700 text-white placeholder:text-blue-400 focus:ring-blue-500'
              : 'bg-indigo-50 border border-indigo-200 text-gray-900 placeholder:text-gray-400 focus:ring-indigo-500'
          }`}
        />
        <button
          type="submit"
          disabled={!inputMessage.trim()}
          className={`rounded-lg p-2 transition-colors disabled:opacity-50 ${
            isDark
              ? 'bg-white text-blue-900 hover:bg-gray-100 disabled:hover:bg-white'
              : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:hover:bg-indigo-600'
          }`}
          aria-label="Send message"
        >
          <Send className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
}
